import React, { useState, useEffect } from 'react';
import { Save, FileText, Calendar, CheckCircle, AlertCircle, Eye, Code } from 'lucide-react';
import { JsonFile } from '../types';
import { VisualEditor } from './visual-editor';
import { ImageService } from '../services';

interface FileEditorProps {
  selectedFile: string | null;
  fileContent: JsonFile | null;
  onSave: (content: any) => void;
  loading: boolean;
  imageService?: ImageService;
}

export const FileEditor: React.FC<FileEditorProps> = ({
  selectedFile,
  fileContent,
  onSave,
  loading,
  imageService,
}) => {
  const [jsonContent, setJsonContent] = useState<any>(null);
  const [jsonString, setJsonString] = useState('');
  const [isValidJson, setIsValidJson] = useState(true);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'text' | 'visual'>('visual');

  useEffect(() => {
    if (fileContent) {
      setJsonContent(fileContent.content);
      setJsonString(JSON.stringify(fileContent.content, null, 2));
      setIsValidJson(true);
      setJsonError(null);
    } else {
      setJsonContent(null);
      setJsonString('');
      setIsValidJson(true);
      setJsonError(null);
    }
  }, [fileContent]);

  const handleJsonChange = (value: string) => {
    setJsonString(value);
    try {
      const parsed = JSON.parse(value);
      setJsonContent(parsed);
      setIsValidJson(true);
      setJsonError(null);
    } catch (error) {
      setIsValidJson(false);
      setJsonError(error instanceof Error ? error.message : 'Error de sintaxis JSON');
    }
  };

  const handleSave = () => {
    if (isValidJson && jsonContent !== null) {
      onSave(jsonContent);
    }
  };

  const handleVisualEditorChange = (newContent: any) => {
    setJsonContent(newContent);
    setJsonString(JSON.stringify(newContent, null, 2));
    setIsValidJson(true);
    setJsonError(null);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (!selectedFile) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
          <FileText size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>Selecciona un archivo</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>
            Elige un archivo JSON de la lista para editarlo
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
            {selectedFile}
          </h2>
          {fileContent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '14px' }}>
              <Calendar size={14} />
              <span>Última modificación: {formatDate(fileContent.lastModified)}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div className="editor-mode-toggle">
            <button
              className={`btn btn-sm ${editorMode === 'visual' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setEditorMode('visual')}
            >
              <Eye size={14} />
              Visual
            </button>
            <button
              className={`btn btn-sm ${editorMode === 'text' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setEditorMode('text')}
            >
              <Code size={14} />
              JSON
            </button>
          </div>
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={loading || !isValidJson}
          >
            <Save size={16} />
            Guardar
          </button>
        </div>
      </div>

      {jsonError && editorMode === 'text' && (
        <div className="error" style={{ marginBottom: '16px' }}>
          <strong>Error de sintaxis JSON:</strong> {jsonError}
        </div>
      )}

      {editorMode === 'visual' ? (
        <VisualEditor
          data={jsonContent}
          onChange={handleVisualEditorChange}
          loading={loading}
          imageService={imageService}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '8px',
            padding: '8px 12px',
            backgroundColor: isValidJson ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${isValidJson ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {isValidJson ? (
              <>
                <CheckCircle size={16} color="#16a34a" />
                <span style={{ color: '#16a34a' }}>JSON válido</span>
              </>
            ) : (
              <>
                <AlertCircle size={16} color="#dc2626" />
                <span style={{ color: '#dc2626' }}>Error de sintaxis JSON</span>
              </>
            )}
          </div>
          
          <textarea
            value={jsonString}
            onChange={(e) => handleJsonChange(e.target.value)}
            style={{
              width: '100%',
              minHeight: '500px',
              padding: '16px',
              border: `2px solid ${isValidJson ? '#d1d5db' : '#ef4444'}`,
              borderRadius: '6px',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              resize: 'vertical',
              backgroundColor: isValidJson ? 'white' : '#fef2f2',
              outline: 'none',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
            placeholder="Escribe tu JSON aquí..."
            onFocus={(e) => {
              e.target.style.borderColor = isValidJson ? '#3b82f6' : '#ef4444';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isValidJson ? '#d1d5db' : '#ef4444';
            }}
          />
          
          {loading && (
            <div style={{
              position: 'absolute',
              top: '60px',
              right: '16px',
              background: 'rgba(59, 130, 246, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                border: '2px solid white',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              Guardando...
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280' }}>
        <p style={{ margin: 0 }}>
          <strong>Tipos de datos soportados:</strong> objetos, arrays, strings, números, booleanos, null
        </p>
        <p style={{ margin: '4px 0 0 0' }}>
          <strong>Formato:</strong> El JSON se formatea automáticamente al guardar
        </p>
      </div>
    </div>
  );
};
